import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { MobileLayout } from "@/components/Layout";
import { getRoleLabel } from "@/lib/roles";
import {
  FolderKanban,
  ClipboardList,
  AlertTriangle,
  CheckCircle,
  Clock,
  Wrench,
  ChevronRight,
  HardHat,
  Plus,
  Users,
  FileText,
  Camera,
  Eye,
  MapPin,
  Calendar,
  Building2,
  CircleDot,
  Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const frenchDate = () => {
  const days = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
  const months = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
  const now = new Date();
  return `${days[now.getDay()]} ${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const months = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [projectProgress, setProjectProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const isSuperAdmin = user?.role === "super_admin";

      if (isSuperAdmin) {
        const res = await api.get("/stats/dashboard");
        setStats(res.data);
      } else {
        const requests = [api.get("/stats/dashboard"), api.get("/projects"), api.get("/visits"), api.get("/observations")];
        const isAdmin = user?.role === "admin_entreprise";
        if (isAdmin) requests.push(api.get("/users"));
        const results = await Promise.all(requests);
        setStats(results[0].data);
        setRecentProjects(results[1].data);

        // Build progress map: project_id -> { ok, total }
        const visits = results[2].data || [];
        const observations = results[3].data || [];
        const visitToProject = {};
        visits.forEach(v => { visitToProject[v.id] = v.project_id; });
        const progressMap = {};
        observations.forEach(obs => {
          const projId = visitToProject[obs.visit_id];
          if (!projId) return;
          if (!progressMap[projId]) progressMap[projId] = { ok: 0, total: 0 };
          progressMap[projId].total++;
          if (obs.status === "OK" || obs.status === "Corrigé") progressMap[projId].ok++;
        });
        setProjectProgress(progressMap);

        if (results[4]) setTeamMembers(results[4].data);
      }
    } catch (error) {
      console.error("Error loading dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MobileLayout title="Tableau de bord">
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </MobileLayout>
    );
  }

  const role = user?.role;

  return (
    <MobileLayout title="Tableau de bord">
      <div className="px-4 lg:px-6 py-5 animate-fade-in">
        <WelcomeHeader user={user} />
        {role === "super_admin" && (
          <SuperAdminDashboard stats={stats} navigate={navigate} />
        )}
        {role === "admin_entreprise" && (
          <AdminDashboard stats={stats} projects={recentProjects} team={teamMembers} navigate={navigate} user={user} projectProgress={projectProgress} />
        )}
        {role === "conducteur_travaux" && (
          <ConducteurDashboard stats={stats} projects={recentProjects} navigate={navigate} projectProgress={projectProgress} />
        )}
        {role === "chef_chantier" && (
          <ChefChantierDashboard stats={stats} projects={recentProjects} navigate={navigate} projectProgress={projectProgress} />
        )}
      </div>
    </MobileLayout>
  );
};

/* ═══════════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════════ */

const WelcomeHeader = ({ user }) => {
  const displayName = user?.first_name || user?.email?.split("@")[0];
  const logoUrl = user?.company_logo_url ? `${BACKEND_URL}${user.company_logo_url}` : null;
  const isSuperAdmin = user?.role === "super_admin";
  return (
    <div className="flex items-center gap-4 mb-6" data-testid="welcome-banner">
      <div className="flex-shrink-0">
        {logoUrl ? (
          <img src={logoUrl} alt="Logo" className="w-12 h-12 lg:w-14 lg:h-14 rounded-xl object-cover border border-zinc-800" />
        ) : (
          <div className={`w-12 h-12 lg:w-14 lg:h-14 rounded-xl flex items-center justify-center ${isSuperAdmin ? "bg-red-500/20" : "bg-zinc-800"}`}>
            {isSuperAdmin ? <Shield size={24} className="text-red-400" /> : <HardHat size={24} className="text-orange-500" />}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-lg font-semibold truncate" data-testid="welcome-name">
          {isSuperAdmin ? `Bonjour, ${displayName}` : `Bonjour ${displayName},`}
        </p>
        <p className="text-zinc-500 text-sm" data-testid="welcome-date">
          {isSuperAdmin ? frenchDate() : "voici l'état de vos chantiers aujourd'hui"}
        </p>
        {!isSuperAdmin && (
          <p className="text-zinc-600 text-xs mt-0.5">{frenchDate()}</p>
        )}
      </div>
      <div className="hidden lg:block text-right flex-shrink-0">
        {isSuperAdmin ? (
          <p className="text-red-400 text-sm font-medium">Super Administrateur</p>
        ) : user?.company_name ? (
          <>
            <p className="text-zinc-300 text-sm font-medium">{user.company_name}</p>
            <p className="text-zinc-500 text-xs">{getRoleLabel(user.role)}</p>
          </>
        ) : null}
      </div>
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div className={`stat-card ${accent === "red" ? "border-red-900/50" : accent === "orange" ? "border-orange-900/50" : accent === "green" ? "border-green-900/50" : accent === "amber" ? "border-amber-900/50" : ""}`} data-testid={testId}>
    <div className="flex items-center gap-2 mb-1.5">
      <Icon size={16} className={accent === "red" ? "text-red-400" : accent === "orange" ? "text-orange-400" : accent === "green" ? "text-green-400" : accent === "amber" ? "text-amber-400" : "text-orange-500"} />
      <span className="text-zinc-400 text-xs uppercase tracking-wider">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${accent === "red" ? "text-red-400" : accent === "orange" ? "text-orange-400" : accent === "green" ? "text-green-400" : accent === "amber" ? "text-amber-400" : "text-white"}`}>{value}</p>
  </div>
);

const SectionTitle = ({ children, action, actionLabel, testId }) => (
  <div className="flex items-center justify-between mb-3">
    <h3 className="text-sm text-zinc-400 uppercase tracking-wider font-medium">{children}</h3>
    {action && (
      <button onClick={action} className="text-orange-500 text-sm font-medium hover:text-orange-400 transition-colors" data-testid={testId}>
        {actionLabel}
      </button>
    )}
  </div>
);

const ProjectRow = ({ project, navigate, progress }) => {
  const pct = progress && progress.total > 0 ? Math.round((progress.ok / progress.total) * 100) : null;
  const barColor = pct === null ? "" : pct >= 75 ? "bg-green-500" : pct >= 50 ? "bg-amber-500" : "bg-orange-500";

  return (
    <button key={project.id} onClick={() => navigate(`/projects/${project.id}`)} className="card-interactive w-full p-4 text-left" data-testid={`project-card-${project.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-semibold truncate">{project.name}</h4>
          <p className="text-zinc-400 text-sm truncate">{project.address}</p>
          <p className="text-zinc-500 text-xs mt-1">{project.client}</p>
        </div>
        <ChevronRight size={20} className="text-zinc-600 flex-shrink-0 ml-3" />
      </div>
      {pct !== null && (
        <div className="mt-3" data-testid={`progress-${project.id}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-zinc-500 text-xs">{progress.ok}/{progress.total} conformes</span>
            <span className={`text-xs font-semibold ${pct >= 75 ? "text-green-400" : pct >= 50 ? "text-amber-400" : "text-orange-400"}`}>{pct}%</span>
          </div>
          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
    </button>
  );
};

const EmptyState = ({ icon: Icon, text }) => (
  <div className="empty-state py-10">
    <Icon size={40} className="mx-auto mb-3 text-zinc-600" />
    <p className="text-zinc-500">{text}</p>
  </div>
);

/* ═══════════════════════════════════════════════
   0. SUPER ADMIN DASHBOARD — Platform only
   ═══════════════════════════════════════════════ */

const STATUS_CONFIG = {
  actif: { label: "Actif", color: "bg-green-500/20 text-green-400 border-green-500/30", dot: "text-green-400" },
  essai: { label: "Essai", color: "bg-amber-500/20 text-amber-400 border-amber-500/30", dot: "text-amber-400" },
  suspendu: { label: "Suspendu", color: "bg-red-500/20 text-red-400 border-red-500/30", dot: "text-red-400" },
};

const SuperAdminDashboard = ({ stats, navigate }) => {
  const sc = stats?.status_counts || {};
  const summaries = stats?.company_summaries || [];

  return (
    <>
      {/* Platform KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="sa-dashboard-stats">
        <KpiCard icon={Building2} label="Entreprises" value={stats?.companies || 0} testId="kpi-entreprises" />
        <KpiCard icon={CircleDot} label="Actives" value={sc.actif || 0} accent="green" testId="kpi-actives" />
        <KpiCard icon={CircleDot} label="Essai" value={sc.essai || 0} accent="amber" testId="kpi-essai" />
        <KpiCard icon={CircleDot} label="Suspendues" value={sc.suspendu || 0} accent="red" testId="kpi-suspendues" />
        <KpiCard icon={Users} label="Utilisateurs" value={stats?.users || 0} testId="kpi-utilisateurs" />
      </div>

      {/* Quick actions */}
      <div className="flex gap-3 mb-6">
        <Button onClick={() => navigate("/super-admin")} className="btn-primary gap-2 h-11 text-sm" data-testid="sa-go-platform">
          <Shield size={18} /> Gestion plateforme
        </Button>
      </div>

      {/* Company list */}
      <section data-testid="section-entreprises">
        <SectionTitle action={() => navigate("/super-admin")} actionLabel="Gerer" testId="sa-manage-companies">Entreprises</SectionTitle>
        {summaries.length === 0 ? (
          <EmptyState icon={Building2} text="Aucune entreprise" />
        ) : (
          <div className="space-y-2">
            {summaries.map((c) => {
              const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.actif;
              return (
                <button key={c.id} onClick={() => navigate("/super-admin")} className="card-interactive w-full p-4 text-left" data-testid={`sa-company-card-${c.id}`}>
                  <div className="flex items-center gap-3">
                    {c.logo_url ? (
                      <img src={`${BACKEND_URL}${c.logo_url}`} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 size={18} className="text-zinc-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-white font-semibold truncate">{c.name}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded border font-medium ${cfg.color}`}>{cfg.label}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        {c.admin_name && <span className="text-zinc-500 text-xs">Admin: {c.admin_name}</span>}
                        <span className="text-zinc-600 text-xs">{c.user_count} utilisateur{c.user_count > 1 ? "s" : ""}</span>
                      </div>
                    </div>
                    <ChevronRight size={18} className="text-zinc-600 flex-shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
};

/* ═══════════════════════════════════════════════
   1. ADMIN ENTREPRISE DASHBOARD
   ═══════════════════════════════════════════════ */

const StatusCard = ({ icon: Icon, color, bg, label, count }) => (
  <div className={`flex items-center gap-2.5 p-3 rounded-lg ${bg} border border-zinc-800/50`} data-testid={`status-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <Icon size={16} className={color} />
    <div>
      <p className="text-white font-semibold text-sm">{count}</p>
      <p className="text-zinc-400 text-xs">{label}</p>
    </div>
  </div>
);

const ObservationRow = ({ obs, navigate }) => {
  const statusStyles = {
    "OK": "bg-green-500/20 text-green-400 border-green-500/30",
    "A corriger": "bg-orange-500/20 text-orange-400 border-orange-500/30",
    "Urgent": "bg-red-500/20 text-red-400 border-red-500/30",
    "Corrige": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  };
  const style = statusStyles[obs.status] || statusStyles["OK"];
  return (
    <button onClick={() => obs.visit_id && navigate(`/visits/${obs.visit_id}`)} className="card-interactive w-full p-3 text-left" data-testid={`obs-row-${obs.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm font-medium truncate">{obs.description}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {obs.project_name && <span className="text-zinc-500 text-xs">{obs.project_name}</span>}
            {obs.location && <span className="text-zinc-600 text-xs">| {obs.location}</span>}
          </div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded border whitespace-nowrap flex-shrink-0 ${style}`}>{obs.status}</span>
      </div>
    </button>
  );
};

const AdminDashboard = ({ stats, projects, team, navigate, user, projectProgress }) => {
  const urgent = stats?.status_stats?.["Urgent"] || 0;
  const aCorriger = stats?.status_stats?.["À corriger"] || 0;
  const recentObs = stats?.recent_observations || [];
  const recentVisits = stats?.recent_visits || [];
  const obsToFix = recentObs.filter(o => o.status === "A corriger" || o.status === "Urgent");

  return (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="admin-stats-grid">
        <KpiCard icon={FolderKanban} label="Projets actifs" value={stats?.projects || 0} testId="kpi-projets-actifs" />
        <KpiCard icon={Wrench} label="A corriger" value={aCorriger} accent={aCorriger > 0 ? "orange" : null} testId="kpi-a-corriger" />
        <KpiCard icon={AlertTriangle} label="Urgences" value={urgent} accent={urgent > 0 ? "red" : null} testId="kpi-urgences" />
        <KpiCard icon={Users} label="Utilisateurs" value={stats?.users || 0} testId="kpi-utilisateurs" />
      </div>

      <div className="lg:grid lg:grid-cols-5 lg:gap-6 space-y-5 lg:space-y-0">
        <div className="lg:col-span-3 space-y-5">
          <div className="lg:hidden">
            {user?.company_name && (
              <p className="text-orange-500 text-sm font-medium">{user.company_name} — {getRoleLabel(user.role)}</p>
            )}
          </div>
          <section data-testid="section-observations-corriger">
            <SectionTitle>Observations a corriger</SectionTitle>
            {obsToFix.length === 0 ? (
              <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-6 text-center">
                <CheckCircle size={32} className="mx-auto mb-2 text-green-500/60" />
                <p className="text-zinc-500 text-sm">Aucune observation en attente</p>
              </div>
            ) : (
              <div className="space-y-2">
                {obsToFix.slice(0, 5).map(obs => <ObservationRow key={obs.id} obs={obs} navigate={navigate} />)}
              </div>
            )}
          </section>
          <section data-testid="section-projets-actifs">
            <SectionTitle action={() => navigate("/projects")} actionLabel="Voir tout" testId="view-all-projects">Projets actifs</SectionTitle>
            {projects.length === 0 ? (
              <EmptyState icon={FolderKanban} text="Aucun projet" />
            ) : (
              <div className="space-y-2">
                {projects.slice(0, 4).map(p => <ProjectRow key={p.id} project={p} navigate={navigate} progress={projectProgress[p.id]} />)}
              </div>
            )}
          </section>
        </div>
        <div className="lg:col-span-2 space-y-5">
          {stats?.status_stats && (
            <section data-testid="section-statuts">
              <SectionTitle>Statuts</SectionTitle>
              <div className="grid grid-cols-2 gap-2">
                <StatusCard icon={CheckCircle} color="text-green-400" bg="bg-green-950/50" label="OK" count={stats.status_stats["OK"] || 0} />
                <StatusCard icon={Wrench} color="text-orange-400" bg="bg-orange-950/50" label="A corriger" count={stats.status_stats["À corriger"] || 0} />
                <StatusCard icon={AlertTriangle} color="text-red-400" bg="bg-red-950/50" label="Urgent" count={stats.status_stats["Urgent"] || 0} />
                <StatusCard icon={Clock} color="text-blue-400" bg="bg-blue-950/50" label="Corrige" count={stats.status_stats["Corrigé"] || 0} />
              </div>
            </section>
          )}
          <section data-testid="section-derniers-rapports">
            <SectionTitle>Derniers rapports</SectionTitle>
            {recentVisits.length === 0 ? (
              <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-6 text-center">
                <FileText size={32} className="mx-auto mb-2 text-zinc-600" />
                <p className="text-zinc-500 text-sm">Aucun rapport</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentVisits.map(v => (
                  <button key={v.id} onClick={() => navigate(`/visits/${v.id}`)} className="card-interactive w-full p-3 text-left" data-testid={`visit-row-${v.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm font-medium truncate">{v.project_name || "Projet"}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-zinc-500 text-xs flex items-center gap-1"><Calendar size={12} />{formatDate(v.date)}</span>
                          <span className="text-zinc-500 text-xs">{v.observations_count || 0} obs.</span>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-zinc-600 flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
          {team.length > 0 && (
            <section data-testid="section-equipe">
              <SectionTitle action={() => navigate("/admin")} actionLabel="Gerer" testId="manage-team">Equipe ({team.length})</SectionTitle>
              <div className="space-y-1.5">
                {team.slice(0, 6).map(m => (
                  <div key={m.id} className="flex items-center justify-between bg-zinc-900/60 border border-zinc-800/50 rounded-lg px-3 py-2">
                    <p className="text-zinc-200 text-sm truncate min-w-0">{m.first_name || m.email.split("@")[0]}</p>
                    <span className={`text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap ${
                      m.role === "admin_entreprise" ? "bg-orange-500/20 text-orange-400" :
                      m.role === "conducteur_travaux" ? "bg-blue-500/20 text-blue-400" :
                      "bg-emerald-500/20 text-emerald-400"
                    }`}>
                      {m.role === "admin_entreprise" ? "Admin" : m.role === "conducteur_travaux" ? "Conducteur" : "Chef"}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════
   2. CONDUCTEUR DE TRAVAUX DASHBOARD
   ═══════════════════════════════════════════════ */

const ConducteurDashboard = ({ stats, projects, navigate, projectProgress }) => {
  const urgent = stats?.status_stats?.["Urgent"] || 0;
  const aCorriger = stats?.status_stats?.["À corriger"] || 0;
  const recentObs = stats?.recent_observations || [];
  const firstProject = projects[0];

  return (
    <>
      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="conducteur-stats-grid">
        <KpiCard icon={FolderKanban} label="Projets" value={stats?.projects || 0} testId="kpi-projets" />
        <KpiCard icon={Wrench} label="A corriger" value={aCorriger} accent={aCorriger > 0 ? "orange" : null} testId="kpi-a-corriger" />
        <KpiCard icon={AlertTriangle} label="Urgences" value={urgent} accent={urgent > 0 ? "red" : null} testId="kpi-urgences" />
      </div>
      <section className="mb-6" data-testid="section-actions-rapides">
        <SectionTitle>Actions rapides</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          {firstProject ? (
            <Button onClick={() => navigate(`/projects/${firstProject.id}`)} className="btn-primary justify-start gap-2 h-12 text-sm" data-testid="quick-new-visit">
              <Plus size={18} /> Nouvelle visite
            </Button>
          ) : (
            <Button onClick={() => navigate("/projects")} className="btn-primary justify-start gap-2 h-12 text-sm" data-testid="quick-new-project">
              <Plus size={18} /> Nouveau projet
            </Button>
          )}
          <Button onClick={() => navigate("/projects")} variant="outline" className="btn-secondary justify-start gap-2 h-12 text-sm" data-testid="quick-view-projects">
            <Eye size={18} /> Voir les projets
          </Button>
        </div>
      </section>
      <div className="lg:grid lg:grid-cols-2 lg:gap-6 space-y-5 lg:space-y-0">
        <section data-testid="section-projets-en-cours">
          <SectionTitle action={() => navigate("/projects")} actionLabel="Voir tout" testId="view-all-projects">Projets en cours</SectionTitle>
          {projects.length === 0 ? (
            <EmptyState icon={FolderKanban} text="Aucun projet" />
          ) : (
            <div className="space-y-2">
              {projects.slice(0, 5).map(p => <ProjectRow key={p.id} project={p} navigate={navigate} progress={projectProgress[p.id]} />)}
            </div>
          )}
        </section>
        <section data-testid="section-dernieres-observations">
          <SectionTitle>Dernieres observations</SectionTitle>
          {recentObs.length === 0 ? (
            <EmptyState icon={ClipboardList} text="Aucune observation" />
          ) : (
            <div className="space-y-2">
              {recentObs.slice(0, 6).map(obs => <ObservationRow key={obs.id} obs={obs} navigate={navigate} />)}
            </div>
          )}
        </section>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════
   3. CHEF DE CHANTIER DASHBOARD
   ═══════════════════════════════════════════════ */

const ChefChantierDashboard = ({ stats, projects, navigate, projectProgress }) => {
  const recentObs = stats?.recent_observations || [];
  const recentPhotos = recentObs.flatMap(o => (o.photos || []).map(url => ({ url, obs: o }))).slice(0, 6);
  const firstProject = projects[0];

  return (
    <>
      <section className="mb-6" data-testid="section-action-principale">
        {firstProject ? (
          <Button onClick={() => navigate(`/projects/${firstProject.id}`)} className="btn-primary w-full justify-center gap-2 h-14 text-base font-semibold" data-testid="quick-add-observation">
            <Camera size={22} /> Ajouter une observation
          </Button>
        ) : (
          <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-6 text-center">
            <FolderKanban size={32} className="mx-auto mb-2 text-zinc-600" />
            <p className="text-zinc-400 text-sm">Aucun projet assigne</p>
          </div>
        )}
      </section>
      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="chef-stats-grid">
        <KpiCard icon={FolderKanban} label="Projets" value={stats?.projects || 0} testId="kpi-projets" />
        <KpiCard icon={ClipboardList} label="Observations" value={stats?.observations || 0} testId="kpi-observations" />
        <KpiCard icon={AlertTriangle} label="Urgences" value={stats?.status_stats?.["Urgent"] || 0} accent={(stats?.status_stats?.["Urgent"] || 0) > 0 ? "red" : null} testId="kpi-urgences" />
      </div>
      <section className="mb-6" data-testid="section-photos-terrain">
        <SectionTitle>Photos terrain</SectionTitle>
        {recentPhotos.length === 0 ? (
          <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-6 text-center">
            <Camera size={32} className="mx-auto mb-2 text-zinc-600" />
            <p className="text-zinc-500 text-sm">Aucune photo recente</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {recentPhotos.map((photo, idx) => (
              <button key={idx} onClick={() => photo.obs.visit_id && navigate(`/visits/${photo.obs.visit_id}`)} className="relative aspect-square rounded-lg overflow-hidden border border-zinc-800 hover:border-orange-500/50 transition-colors group" data-testid={`photo-${idx}`}>
                <img src={photo.url.startsWith("http") ? photo.url : `${BACKEND_URL}${photo.url}`} alt="Terrain" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                  <p className="text-white text-xs truncate">{photo.obs.project_name || ""}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>
      <section data-testid="section-mes-chantiers">
        <SectionTitle action={() => navigate("/projects")} actionLabel="Voir tout" testId="view-all-projects">Mes chantiers</SectionTitle>
        {projects.length === 0 ? (
          <EmptyState icon={FolderKanban} text="Aucun chantier" />
        ) : (
          <div className="space-y-2">
            {projects.slice(0, 3).map(p => {
              const progress = projectProgress[p.id];
              const pct = progress && progress.total > 0 ? Math.round((progress.ok / progress.total) * 100) : null;
              const barColor = pct === null ? "" : pct >= 75 ? "bg-green-500" : pct >= 50 ? "bg-amber-500" : "bg-orange-500";
              return (
                <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="card-interactive w-full p-4 text-left" data-testid={`project-card-${p.id}`}>
                  <h4 className="text-white font-semibold truncate">{p.name}</h4>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-zinc-400 text-xs flex items-center gap-1"><MapPin size={12} />{p.address}</span>
                  </div>
                  <p className="text-zinc-500 text-xs mt-1">{p.client}</p>
                  {pct !== null && (
                    <div className="mt-3" data-testid={`progress-${p.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-zinc-500 text-xs">{progress.ok}/{progress.total} conformes</span>
                        <span className={`text-xs font-semibold ${pct >= 75 ? "text-green-400" : pct >= 50 ? "text-amber-400" : "text-orange-400"}`}>{pct}%</span>
                      </div>
                      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
};

export default DashboardPage;
